import { useEffect, useState } from "react";
import { cacheService, thumbnailService, titleService } from "../../../Core/Api/services";
import { CatalogReportTitleInfos } from "../../../Core/Api/Types/catalog-reporting-response";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import PageFilter from "../../../Components/Misc/PageFilter";
import { Download, Inventory2Outlined } from "@mui/icons-material";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import StorageIcon from "@mui/icons-material/Storage";
import MovieIcon from "@mui/icons-material/Movie";
import AutocompleteSearchCatalog from "../../../Components/Search/AutocompleteSearchCatalog/autocomplete-search-catalog";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import AssetClassChip from "../../../Components/Misc/Chips/AssetChip/asset-class-chip";
import { SingleLabelValue } from "../../Orders/OrderWatermarks/order-watermarks";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";

import CloseIcon from "@mui/icons-material/Close";
import { routePaths } from "../../../Router/router";
import EmptyListSlot from "../../../Components/Misc/EmptyListSlot";
import SearchIcon from "@mui/icons-material/Search";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { AclMatch, UserHasAcl, UserHasAcls } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { useNavigate } from "react-router";

const BYTES_IN_GIGA = Math.pow(1024, 3);
const BYTES_IN_TERA = Math.pow(1024, 4);

const ReportingCatalog: React.FC<{}> = ({}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [titlesInfos, setTitlesInfos] = useState<CatalogReportTitleInfos>();
  const [titleInfosList, setTitleInfosList] = useState<CatalogReportTitleInfos[]>([]);
  const [loading, setLoading] = useState(true);
  const showTotalDiskSize = false;
  useEffect(() => {
    if (
      !UserHasAcls([AclTypes.aclMetricView, AclTypes.aclSuperUser], AclMatch.OneAtLeast) ||
      cacheService.GetPlatformMode() !== PlatformMode.Distribution
    )
      navigate(routePaths.home.path);

    titleService.GetCatalogReporting().then((response) => {
      setLoading(false);
      if (response.success && response.titlesInfo) {
        setTitlesInfos(response.titlesInfo);
      }
    });
  }, []);

  const handleValueProposalClick = (asset: DbAssetObjectInfos) => {
    if (titleInfosList.find((x) => x.titleId === asset.id) !== undefined) return;

    titleService.GetCatalogReportingByTitle(asset.id).then((response) => {
      if (response.titlesInfo) {
        setTitleInfosList((x) => [{ ...response.titlesInfo!, title: asset }, ...x]);
      }
    });
  };
  const customSnack = useCustomSnackbar();

  const handleClickExport = () => {
    titleService.DownloadCatalogReportAsCSV().then((response) => {
      if (response.success) {
        customSnack(t("alert:success.exportStart"), "success");
      } else {
        customSnack(response.message ?? t("alert:error.exportCatalogData"), "error");
      }
    });
  };

  if (loading)
    return (
      <Box
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        height={"calc(100vh - 89px)"}
        width={"100%"}
      >
        <CircularProgress />
      </Box>
    );

  return (
    <Stack
      height={"calc(100vh - 89px)"}
      overflow={"hidden"}
      pl={2}
      pr={1}
      pt={2}
      pb={0}
      width={"100%"}
      direction={"column"}
    >
      {titlesInfos && (
        <Stack width={"100%"} direction={"column"}>
          <Grid spacing={1} width={"100%"} container mb={1}>
            <Grid key={"titles"} flex={1} item md={3}>
              <PageFilter
                small
                title={titlesInfos.titleCount ?? 0}
                subtitle={t("page:pageFilter.subTitle.totalTitles")}
                icon={<MovieIcon />}
                variant="primary"
              />
            </Grid>
            <Grid key={"hotStorage"} flex={1} item md={showTotalDiskSize ? 3 : 6}>
              <PageFilter
                small
                title={(titlesInfos.sizeHot / BYTES_IN_TERA).toFixed(2)}
                subtitle={t("page:pageFilter.subTitle.totalHotStorage")}
                icon={<StorageIcon />}
                variant="primary"
              />
            </Grid>
            {showTotalDiskSize && (
              <Grid key={"Disk"} flex={1} item md={3}>
                <PageFilter
                  small
                  title={(titlesInfos.sizeDisk / BYTES_IN_TERA).toFixed(2)}
                  subtitle={t("page:pageFilter.subTitle.totalDiskSize")}
                  icon={<StorageIcon />}
                  variant="primary"
                />
              </Grid>
            )}
            <Grid key={"archive"} flex={1} item md={showTotalDiskSize ? 3 : 6}>
              <PageFilter
                small
                title={(titlesInfos.sizeArchive / BYTES_IN_TERA).toFixed(2)}
                subtitle={t("page:pageFilter.subTitle.totalArchiveStorageSize")}
                icon={<StorageIcon />}
                variant="primary"
              />
            </Grid>
          </Grid>
          <Grid spacing={1} width={"100%"} container mb={1}>
            <Grid item md={2.4} sm={4}>
              <PageFilter
                small
                title={titlesInfos.assets}
                subtitle={t("page:pageFilter.subTitle.totalMaterial", { material: "Assets" })}
                icon={<Inventory2Outlined />}
              />
            </Grid>
            <Grid item md={2.4} sm={4}>
              <PageFilter
                small
                title={titlesInfos.mezz}
                subtitle={t("page:pageFilter.subTitle.totalMaterial", { material: "MEZZ" })}
                icon={<AssetClassChip classification={ObjectClass.Mezz} />}
              />
            </Grid>
            <Grid item md={2.4} sm={4}>
              <PageFilter
                small
                title={titlesInfos.raw}
                subtitle={t("page:pageFilter.subTitle.totalMaterial", { material: "RAW" })}
                icon={<AssetClassChip classification={ObjectClass.Raw} />}
              />
            </Grid>
            <Grid item md={2.4} sm={6}>
              <PageFilter
                small
                title={titlesInfos.dcp}
                subtitle={t("page:pageFilter.subTitle.totalMaterial", { material: "DCP" })}
                icon={<AssetClassChip classification={ObjectClass.Dcp} />}
              />
            </Grid>
            <Grid item md={2.4} sm={6}>
              <PageFilter
                small
                title={titlesInfos.imf}
                subtitle={t("page:pageFilter.subTitle.totalMaterial", { material: "IMF" })}
                icon={<AssetClassChip classification={ObjectClass.Imf} />}
              />
            </Grid>
          </Grid>
        </Stack>
      )}
      <Stack
        sx={{
          background: theme.palette.background.default,
          pb: "8px",
          pr: "8px",
          width: "100%",
        }}
        justifyContent={"space-between"}
        zIndex={theme.zIndex.drawer - 1}
        direction={"row"}
        spacing={1}
      >
        <Stack alignItems={"center"} direction={"row"} spacing={1}>
          <AutocompleteSearchCatalog
            width={256}
            enableSearchCallback
            layer={ObjectLayer.Title}
            onValueProposalClick={handleValueProposalClick}
          />
          {titleInfosList.length > 1 && <Typography color={"text.tier"}>{t("page:body.totalInSearch")}</Typography>}
          {titleInfosList.length > 1 && (
            <Chip label={t("page:body.titlesWithCount", { count: titleInfosList.length })} icon={<MovieIcon />} />
          )}
          {titleInfosList.length > 1 && (
            <Chip
              label={
                "Archive " +
                (titleInfosList.reduce((acc, x) => x.sizeArchive + acc, 0) / BYTES_IN_TERA).toFixed(1) +
                "TB"
              }
              icon={<StorageIcon />}
            />
          )}
          {titleInfosList.length > 1 && (
            <Chip
              label={"Hot " + (titleInfosList.reduce((acc, x) => x.sizeHot + acc, 0) / BYTES_IN_TERA).toFixed(1) + "TB"}
              icon={<StorageIcon />}
            />
          )}
          {titleInfosList.length > 1 && (
            <Chip
              label={titleInfosList.reduce((acc, x) => x.assets + acc, 0) + " assets"}
              icon={<Inventory2Outlined />}
            />
          )}
        </Stack>
        <Button onClick={handleClickExport} color="primary" variant="outlined" endIcon={<Download />}>
          {t("button:exportAllCatalogAsCsv")}
        </Button>
      </Stack>
      <Stack
        sx={{ position: "relative", overflowY: "scroll", ...theme.helpers(theme).scrollBar }}
        direction={"column"}
        spacing={1}
      >
        {titleInfosList.length === 0 && (
          <EmptyListSlot
            icon={<SearchIcon />}
            sx={{ background: theme.palette.background.default }}
            title={t("page:emptyListSlot.title.lookingForSpecificTitle")}
            desc={t("page:emptyListSlot.desc.searchTitleAndViewStorageDetails")}
          />
        )}
        <Stack spacing={1}>
          {titleInfosList.map((x) => (
            <Card key={x.titleId} sx={{ position: "relative" }}>
              <CardHeader
                onClick={(e) => {
                  if ((e.target as any).localName !== "svg")
                    window.open(routePaths.title.path + "/" + x.titleId, "_blank");
                }}
                sx={{
                  py: "8px !important",
                  cursor: "pointer",
                  "&:hover > .MuiCardHeader-content": {
                    textDecoration: "underline",
                  },
                }}
                title={x.title?.name ?? ""}
                action={
                  <IconButton
                    onClick={() => {
                      setTitleInfosList((l) => [...l.filter((t) => t.titleId !== x.titleId)]);
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                }
              />
              <CardContent>
                <Grid container direction={"row"} spacing={2}>
                  <Grid item md={1} sm={3}>
                    <img
                      style={{ width: "100px", height: "150px" }}
                      src={thumbnailService.GetObjectPosterUrl(x.title?.id, ThumbnailSize.small)}
                    />
                  </Grid>
                  <Grid item md={5.5} sm={9}>
                    <Stack direction={"column"} spacing={1}>
                      <SingleLabelValue label={t("field:label.assetsCount")} value={x.assets} />
                      <Divider sx={{ width: "100%" }} variant="middle" orientation="horizontal" />
                      <SingleLabelValue
                        label={t("field:label.archiveStorage")}
                        value={(x.sizeArchive / BYTES_IN_GIGA).toFixed(2)}
                      />
                      <Divider sx={{ width: "100%" }} variant="middle" orientation="horizontal" />
                      <SingleLabelValue
                        label={t("field:label.hotStorage")}
                        value={(x.sizeHot / BYTES_IN_GIGA).toFixed(2)}
                      />
                      <Divider sx={{ width: "100%" }} variant="middle" orientation="horizontal" />
                      <SingleLabelValue
                        label={t("field:label.diskStorage")}
                        value={(x.sizeDisk / BYTES_IN_GIGA).toFixed(2)}
                      />
                    </Stack>
                  </Grid>
                  <Grid item md={5.5} sm={12}>
                    <Stack direction={"column"} spacing={1}>
                      <SingleLabelValue label={"MEZZ"} value={x.mezz} />
                      <Divider sx={{ width: "100%" }} variant="middle" orientation="horizontal" />
                      <SingleLabelValue label={"RAW"} value={x.raw} />
                      <Divider sx={{ width: "100%" }} variant="middle" orientation="horizontal" />
                      <SingleLabelValue label={"DCP"} value={x.dcp} />
                      <Divider sx={{ width: "100%" }} variant="middle" orientation="horizontal" />
                      <SingleLabelValue label={"IMF"} value={x.imf} />
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ReportingCatalog;
